import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";

export function useMonthMetrics(monthKey: string) {
  const [jobId, setJobId] = useState<string | null>(null);
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["metrics", monthKey],
    queryFn: async () => {
      const [year, month] = monthKey.split("/");
      const from = `${year}-${month}-01`;
      
      const nextMonth = new Date(parseInt(year), parseInt(month), 1);
      nextMonth.setDate(nextMonth.getDate() - 1);
      const to = `${year}-${month}-${String(nextMonth.getDate()).padStart(2, '0')}`;
      
      const res = await fetch(`/api/metrics?from=${from}&to=${to}`);
      if (!res.ok) {
        if (res.status === 404) return { phase: "missing" };
        throw new Error("Failed to fetch metrics");
      }
      const json = await res.json();
      return { payload: json.payload, phase: "loaded" };
    },
    retry: false
  });

  const { data: job } = useQuery({
    queryKey: ["job", jobId],
    queryFn: async () => {
      const res = await fetch(`/api/jobs/${jobId}`);
      return res.json();
    },
    enabled: !!jobId,
    refetchInterval: (query) => {
      const status = query.state?.data?.status;
      return status === "running" ? 2000 : false;
    }
  });

  useEffect(() => {
    if (job?.status === "completed") {
      refetch();
      setJobId(null);
    } else if (job?.status === "failed") {
      setJobId(null);
    }
  }, [job?.status, refetch]);

  let phase = data?.phase || (isLoading ? "loading" : "error");
  if (job?.status === "running") phase = "fetching";
  if (job?.status === "failed") phase = "error";

  return {
    payload: data?.payload,
    phase,
    error: error?.message,
    monthLabel: monthKey,
    startSync: async () => {
      const parts = monthKey.replace("%2F", "/").split("/");
      const year = parts[0];
      const month = parts[1] ? parts[1].padStart(2, "0") : "01";
      const from = `${year}-${month}-01`;
      const res = await fetch("/api/jobs/run", { 
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ period_start: from })
      });
      if (res.ok) {
        const json = await res.json();
        setJobId(json.id);
      }
    },
    job
  };
}
